import { type ChangeEvent, type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { helpTopicPath, routes } from '../../routes';
import { SessionApiError } from '../../session/client';
import { type SessionUser } from '../../session/SessionProvider';
import { orderHref } from '../orders/OrdersPage';
import { type Order, getTracking } from '../orders/orders-api';
import { BackLink } from '../panels';
import {
  type Strip,
  type StripIcon,
  appointmentBadge,
  appointmentIcs,
  appointmentPayment,
  appointmentStrip,
  answerText,
  canCancel,
  cancelCopy,
  cancelledNote,
  clockOf,
  consultationBadge,
  consultationOpen,
  consultationPayment,
  consultationState,
  consultationSteps,
  consultationStrip,
  dayOf,
  directionsUrl,
  isUpcoming,
  isVirtual,
  listingPrice,
  longDay,
  minutesOf,
  monthOf,
  placeOf,
  prescriberNote,
  shopTile,
  shortDay,
  stamp,
} from './booking-model';
import {
  type Appointment,
  type Consultation,
  type Listing,
  cancelAppointment,
  getAppointment,
  getConsultation,
  getListing,
  getListings,
  getOrdersById,
  listAppointments,
  listConsultations,
  replyToPrescriber,
  uploadEvidence,
} from './bookings-api';
import '../orders/orders.css';
import './bookings.css';

/**
 * The Bookings page — the `Just Treated Bookings` frame, over the platform's
 * own appointments and online consultations.
 *
 * The list (Upcoming and Past) and one booking's detail, at `/bookings/:id`
 * (a consultation adds `?kind=consultation`). The logged-out panel is the
 * site shell's, with the frame's own copy.
 *
 * Where the frame draws something the platform cannot back, the element keeps
 * its place and says "Coming soon", or is left out — never the frame's demo:
 * no reschedule that moves nothing, no video link, no "Before you go" list the
 * pharmacy never wrote, no 24-hour fee, no "within 2 hours", no prescriber
 * name the platform keeps off the customer's screen. A new appointment is
 * "requested" until the shop accepts it, and a consultation is paid for
 * before a prescriber reviews it (owner decision D9).
 */

const IC = {
  cal: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
  pin: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11ZM12 12.4a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8',
  video: 'M3.5 7h11v10h-11zM14.5 10.5 20.5 7v10l-6-3.5',
  chat: 'M4 5h16v11H8l-4 4Z',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5M12 16.5h.01',
  x: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9 9l6 6M15 9l-6 6',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2',
  redo: 'M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4',
  check: 'm5 12.5 4.2 4.2L19 7',
  card: 'M3.5 6.5h17v11h-17zM3.5 10h17M7 14.5h4',
  bag: 'M5 8h14l-1 12H6ZM9 8V6a3 3 0 0 1 6 0v2',
  warn: 'M12 3.5 2.5 20h19ZM12 10v4.5M12 17.2h.01',
  chevron: 'm6 9 6 6 6-6',
  back: 'M14.6 5.4 8 12l6.6 6.6',
  next: 'M9.4 5.4 16 12l-6.6 6.6',
} as const;

const STRIP_ICON: Readonly<Record<StripIcon, string>> = {
  clock: IC.clock,
  check: IC.check,
  x: IC.x,
  chat: IC.chat,
  card: IC.card,
};

function Icon({
  d,
  stroke = 'currentColor',
  size = 17,
  width = 2,
}: {
  readonly d: string;
  readonly stroke?: string;
  readonly size?: number;
  readonly width?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Soon(): JSX.Element {
  return <span className="jt-ord-soon">Coming soon</span>;
}

function messageOf(error: unknown): string {
  return error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';
}

export const bookingHref = (id: string, kind: 'appointment' | 'consultation' = 'appointment'): string =>
  `${routes.bookings.path}/${encodeURIComponent(id)}${kind === 'consultation' ? '?kind=consultation' : ''}`;

/** Android's short reference for an appointment: the id's last six characters. */
export const appointmentReference = (id: string): string => `JT-APT-${id.slice(-6).toUpperCase()}`;

function storeHref(listing: Listing | undefined, book: boolean): string | undefined {
  if (listing === undefined) return undefined;
  const base = `/storefronts/${encodeURIComponent(listing.storefront.slug)}`;
  return book ? `${base}?book=${encodeURIComponent(listing.id)}` : base;
}

// -- Small hooks ------------------------------------------------------------------------

interface Loaded<T> {
  readonly value?: T;
  readonly error?: Error;
  readonly loading: boolean;
  reload(quiet?: boolean): void;
}

function useLoaded<T>(load: () => Promise<T>): Loaded<T> {
  const [state, setState] = useState<{ value?: T; error?: Error; loading: boolean }>({ loading: true });
  const loadRef = useRef(load);
  loadRef.current = load;
  const alive = useRef(true);

  const reload = useCallback((quiet = false) => {
    if (!quiet) setState((previous) => ({ ...previous, loading: true }));
    loadRef
      .current()
      .then((value) => {
        if (alive.current) setState({ value, loading: false });
      })
      .catch((error: unknown) => {
        if (!alive.current) return;
        const failure = error instanceof Error ? error : new Error('Something went wrong.');
        setState((previous) =>
          quiet && previous.value !== undefined ? previous : { error: failure, loading: false },
        );
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  return { ...state, reload };
}

function useNarrow(): boolean {
  const read = (): boolean => (typeof window === 'undefined' ? false : window.innerWidth < 940);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const onResize = (): void => setNarrow(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

function useToast(): [string, (message: string) => void] {
  const [toast, setToast] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback((message: string) => {
    clearTimeout(timer.current);
    setToast(message);
    timer.current = setTimeout(() => setToast(''), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, flash];
}

function Toast({ message }: { readonly message: string }): JSX.Element | null {
  return message === '' ? null : (
    <div className="jt-ord-toast" role="status">
      {message}
    </div>
  );
}

/** Saves a file the page built. False when the browser refuses. */
function download(name: string, text: string, type: string): boolean {
  try {
    const url = URL.createObjectURL(new Blob([text], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}

// -- Page --------------------------------------------------------------------------------

export function BookingsPage({ user }: { readonly user: SessionUser }): JSX.Element {
  const navigator = useNavigator();
  const path = navigator.location.path;
  const prefix = `${routes.bookings.path}/`;
  if (path.startsWith(prefix)) {
    const id = decodeURIComponent(path.slice(prefix.length));
    const consultation = navigator.location.query.get('kind') === 'consultation';
    return consultation ? (
      <ConsultationDetail key={id} consultationId={id} user={user} />
    ) : (
      <AppointmentDetail key={id} bookingId={id} user={user} />
    );
  }
  return <BookingsList />;
}

// -- The list --------------------------------------------------------------------------------

interface Entry {
  readonly key: string;
  readonly href: string;
  readonly upcoming: boolean;
  /** What the list sorts by: the appointment's time, or when the answers were sent. */
  readonly at: number;
  readonly card: CardView;
}

interface CardView {
  readonly mon: string;
  readonly day: string;
  readonly title: string;
  readonly line: string;
  readonly when: string;
  readonly badge: { label: string; bg: string; fg: string };
  readonly strip?: Strip;
  readonly primary: string;
  readonly secondary: { label: string; to?: string };
}

interface ListData {
  readonly entries: readonly Entry[];
  readonly complete: boolean;
  readonly consultationsFailed: boolean;
}

async function loadList(now: number): Promise<ListData> {
  const [appointments, consultations] = await Promise.all([
    listAppointments(),
    listConsultations().catch(() => undefined),
  ]);
  const listings = await getListings([
    ...appointments.items.map((a) => a.listingId),
    ...(consultations ?? []).map((c) => c.listingId),
  ]);
  const orders =
    consultations?.some((c) => c.orderId !== undefined) === true
      ? await getOrdersById()
      : new Map<string, Order>();

  const entries: Entry[] = [
    ...appointments.items.map((a) => appointmentEntry(a, listings.get(a.listingId), now)),
    ...(consultations ?? []).map((c) =>
      consultationEntry(
        c,
        listings.get(c.listingId),
        c.orderId !== undefined ? orders.get(c.orderId) : undefined,
        now,
      ),
    ),
  ];
  return { entries, complete: appointments.complete, consultationsFailed: consultations === undefined };
}

function appointmentEntry(a: Appointment, listing: Listing | undefined, now: number): Entry {
  const store = listing?.storefront.name ?? 'The pharmacy';
  const upcoming = isUpcoming(a, now);
  const strip = appointmentStrip(a, store, now);
  const again = storeHref(listing, true);
  return {
    key: `a:${a.id}`,
    href: bookingHref(a.id),
    upcoming,
    at: Date.parse(a.startsAt),
    card: {
      mon: monthOf(a.startsAt, a.timeZone),
      day: dayOf(a.startsAt, a.timeZone),
      title: listing?.title ?? 'Appointment',
      line: `${store} · ${isVirtual(a) ? 'Video call' : 'In person'}`,
      when: `${shortDay(a.startsAt, a.timeZone)} · ${clockOf(a.startsAt, a.timeZone)}`,
      badge: appointmentBadge(a, now),
      ...(strip !== undefined ? { strip } : {}),
      primary: 'View details',
      // Reschedule has no platform operation yet; a past booking can be booked again.
      secondary: upcoming
        ? { label: 'Reschedule' }
        : { label: 'Book again', ...(again !== undefined ? { to: again } : {}) },
    },
  };
}

function consultationEntry(
  c: Consultation,
  listing: Listing | undefined,
  order: Order | undefined,
  now: number,
): Entry {
  const store = listing?.storefront.name ?? 'The pharmacy';
  const state = consultationState(c, now);
  return {
    key: `c:${c.id}`,
    href: bookingHref(c.id, 'consultation'),
    upcoming: consultationOpen(c, now),
    at: Date.parse(c.submittedAt),
    card: {
      mon: monthOf(c.submittedAt),
      day: dayOf(c.submittedAt),
      title: c.itemName,
      line: `${store} · Online consultation`,
      when: `Submitted ${stamp(c.submittedAt)}`,
      badge: consultationBadge(c, now),
      strip: consultationStrip(c, order, store, now),
      primary:
        state === 'MORE_INFO'
          ? 'Reply now'
          : state === 'SUBMITTED' || state === 'IN_ORDER'
            ? 'View progress'
            : 'View details',
      secondary: { label: 'Get help', to: helpTopicPath('appt') },
    },
  };
}

function BookingsList(): JSX.Element {
  const [now] = useState(() => Date.now());
  const data = useLoaded(() => loadList(now));
  const [tab, setTab] = useState<'up' | 'past'>('up');

  const entries = data.value?.entries ?? [];
  const upcoming = entries.filter((e) => e.upcoming).sort((a, b) => a.at - b.at);
  const past = entries.filter((e) => !e.upcoming).sort((a, b) => b.at - a.at);
  const list = tab === 'past' ? past : upcoming;

  let body: ReactNode;
  if (data.value === undefined && data.loading) {
    body = (
      <div className="jt-ord-panel" aria-busy="true">
        <span className="jt-ord-panel__copy">Loading your bookings…</span>
      </div>
    );
  } else if (data.value === undefined) {
    body = (
      <div className="jt-ord-panel" role="alert">
        <span className="jt-ord-panel__head">We couldn’t load your bookings</span>
        <span className="jt-ord-panel__copy">{messageOf(data.error)}</span>
        <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={() => data.reload()}>
          Try again
        </button>
      </div>
    );
  } else if (list.length === 0) {
    body = (
      <div className="jt-ord-empty">
        <span className="jt-ord-empty__icon" aria-hidden="true">
          <Icon d={IC.cal} stroke="#5b2ede" size={26} />
        </span>
        <span className="jt-ord-empty__title">
          {tab === 'past' ? 'No past bookings' : 'Nothing booked yet'}
        </span>
        <span className="jt-ord-empty__sub">
          {tab === 'past'
            ? 'Appointments and consultations you’ve had will show here.'
            : 'Book a vaccination, health check or online consultation with a local provider.'}
        </span>
        {routes.nearby.availability === 'available' ? (
          <AppLink to={routes.nearby.path} className="jt-ord-pill jt-ord-pill--ink">
            Find a service
          </AppLink>
        ) : (
          <span className="jt-ord-pill jt-ord-pill--ink jt-ord-pill--off" aria-disabled="true">
            Find a service <Soon />
          </span>
        )}
      </div>
    );
  } else {
    body = (
      <div className="jt-ord-cards">
        {list.map((entry) => (
          <BookingCard key={entry.key} entry={entry} />
        ))}
      </div>
    );
  }

  return (
    <>
      <BackLink />
      <div className="jt-ord-listhead">
        <div className="jt-ord-listhead__text">
          <h1 className="jt-ord-h1">Bookings</h1>
          <span className="jt-ord-listhead__sub">Appointments and online consultations</span>
        </div>
        <div role="tablist" aria-label="Bookings" className="jt-ord-tabs">
          {(
            [
              ['up', `Upcoming · ${String(upcoming.length)}`],
              ['past', `Past · ${String(past.length)}`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`jt-ord-tab${tab === id ? ' jt-ord-tab--on' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {body}
      {data.value?.consultationsFailed === true ? (
        <p className="jt-ord-note" role="status">
          Your online consultations couldn’t be loaded just now, so only appointments are shown.
        </p>
      ) : null}
      {data.value !== undefined && !data.value.complete ? (
        <p className="jt-ord-note">Showing your most recent appointments.</p>
      ) : null}
    </>
  );
}

function BookingCard({ entry }: { readonly entry: Entry }): JSX.Element {
  const navigator = useNavigator();
  const { card } = entry;
  return (
    <article className="jt-ord-card">
      <div className="jt-ord-card__top">
        <span className={`jt-bk-date${entry.upcoming ? ' jt-bk-date--up' : ''}`} aria-hidden="true">
          <span className="jt-bk-date__mon">{card.mon}</span>
          <span className="jt-bk-date__day">{card.day}</span>
        </span>
        <span className="jt-bk-card__text">
          <span className="jt-bk-card__title">{card.title}</span>
          <span className="jt-bk-card__line">{card.line}</span>
          <span className="jt-bk-card__when">{card.when}</span>
        </span>
        <span
          className="jt-ord-badge jt-ord-badge--card"
          style={{ background: card.badge.bg, color: card.badge.fg }}
        >
          {card.badge.label}
        </span>
      </div>
      {card.strip !== undefined ? (
        <div className="jt-ord-strip" style={{ background: card.strip.bg, color: card.strip.fg }}>
          <Icon d={STRIP_ICON[card.strip.icon]} />
          <span>{card.strip.text}</span>
        </div>
      ) : null}
      <div className="jt-ord-card__actions">
        <a
          href={entry.href}
          className="jt-ord-pill jt-ord-pill--ink"
          onClick={(event) => {
            event.preventDefault();
            navigator.navigate(entry.href);
          }}
        >
          {card.primary}
        </a>
        {card.secondary.to !== undefined ? (
          <AppLink to={card.secondary.to} className="jt-ord-pill jt-ord-pill--soft">
            {card.secondary.label}
          </AppLink>
        ) : (
          <span className="jt-ord-pill jt-ord-pill--soft jt-ord-pill--off" aria-disabled="true">
            {card.secondary.label} <Soon />
          </span>
        )}
      </div>
    </article>
  );
}

// -- Shared detail parts --------------------------------------------------------------------

interface Action {
  readonly label: string;
  readonly icon: string;
  readonly danger?: boolean;
  readonly act?: () => void;
}

function Actions({ actions }: { readonly actions: readonly Action[] }): JSX.Element {
  return (
    <div className="jt-ord-actions">
      {actions.map((action) =>
        action.act !== undefined ? (
          <button
            key={action.label}
            type="button"
            className={`jt-ord-action${action.danger === true ? ' jt-ord-action--danger' : ''}`}
            onClick={action.act}
          >
            <span className="jt-ord-action__icon">
              <Icon d={action.icon} stroke={action.danger === true ? '#a81f1f' : '#5b2ede'} />
            </span>
            <span className="jt-ord-action__label">{action.label}</span>
            <Icon d={IC.next} stroke="#8a7fb5" size={14} width={2.6} />
          </button>
        ) : (
          <div key={action.label} className="jt-ord-action jt-ord-action--off" aria-disabled="true">
            <span className="jt-ord-action__icon">
              <Icon d={action.icon} stroke="#8a7fb5" />
            </span>
            <span className="jt-ord-action__label">{action.label}</span>
            <Soon />
          </div>
        ),
      )}
    </div>
  );
}

function Aside({
  storefrontId,
  store,
  addr,
  facts,
  children,
}: {
  readonly storefrontId: string;
  readonly store: string;
  readonly addr: string;
  readonly facts: readonly { k: string; v: string }[];
  readonly children?: ReactNode;
}): JSX.Element {
  const tile = shopTile(storefrontId, store);
  return (
    <aside aria-label="Booking details" className="jt-ord-summary">
      <div className="jt-ord-summary__head">
        <span className="jt-ord-tile jt-ord-tile--42" style={{ background: tile.tile }} aria-hidden="true">
          {tile.initials}
        </span>
        <span className="jt-ord-summary__who">
          <span className="jt-ord-summary__store">{store}</span>
          <span className="jt-ord-summary__meta">{addr}</span>
        </span>
      </div>
      <dl className="jt-bk-facts">
        {facts.map((fact) => (
          <div key={fact.k} className="jt-bk-fact">
            <dt>{fact.k}</dt>
            <dd>{fact.v}</dd>
          </div>
        ))}
      </dl>
      {children}
    </aside>
  );
}

function DetailFrame({
  narrow,
  main,
  side,
}: {
  readonly narrow: boolean;
  readonly main: ReactNode;
  readonly side: ReactNode;
}): JSX.Element {
  const navigator = useNavigator();
  return (
    <div className={`jt-ord-detail${narrow ? ' jt-ord-detail--narrow' : ''}`}>
      <div className="jt-ord-detail__main">
        <a
          href={routes.bookings.path}
          className="jt-site-back"
          onClick={(event) => {
            event.preventDefault();
            navigator.navigate(routes.bookings.path);
          }}
        >
          <Icon d={IC.back} size={15} width={2.6} />
          All bookings
        </a>
        {main}
      </div>
      <div className="jt-ord-detail__side">{side}</div>
    </div>
  );
}

function DetailState({
  loading,
  error,
  what,
  onRetry,
}: {
  readonly loading: boolean;
  readonly error: Error | undefined;
  readonly what: string;
  readonly onRetry: () => void;
}): JSX.Element {
  const navigator = useNavigator();
  const missing = error instanceof SessionApiError && error.status === 404;
  return (
    <>
      <a
        href={routes.bookings.path}
        className="jt-site-back"
        onClick={(event) => {
          event.preventDefault();
          navigator.navigate(routes.bookings.path);
        }}
      >
        <Icon d={IC.back} size={15} width={2.6} />
        All bookings
      </a>
      {loading ? (
        <div className="jt-ord-panel" aria-busy="true">
          <span className="jt-ord-panel__copy">Loading your {what}…</span>
        </div>
      ) : (
        <div className="jt-ord-panel" role="alert">
          <span className="jt-ord-panel__head">
            {missing ? `We couldn’t find this ${what}` : `We couldn’t load this ${what}`}
          </span>
          <span className="jt-ord-panel__copy">
            {missing ? `It isn’t one of your bookings, or the link is wrong.` : messageOf(error)}
          </span>
          {missing ? null : (
            <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      )}
    </>
  );
}

// -- One appointment ----------------------------------------------------------------------------

function AppointmentDetail({
  bookingId,
  user,
}: {
  readonly bookingId: string;
  readonly user: SessionUser;
}): JSX.Element {
  const navigator = useNavigator();
  const narrow = useNarrow();
  const [toast, flash] = useToast();
  const [modal, setModal] = useState(false);
  const loaded = useLoaded(async () => {
    const appointment = await getAppointment(bookingId);
    const listing = await getListing(appointment.listingId).catch(() => undefined);
    return { appointment, listing };
  });

  const value = loaded.value;
  if (value === undefined) {
    return (
      <DetailState
        loading={loaded.loading}
        error={loaded.error}
        what="appointment"
        onRetry={() => loaded.reload()}
      />
    );
  }

  const { appointment: a, listing } = value;
  const now = Date.now();
  const store = listing?.storefront.name ?? 'The pharmacy';
  const title = listing?.title ?? 'Appointment';
  const badge = appointmentBadge(a, now);
  const upcoming = isUpcoming(a, now);
  const virtual = isVirtual(a);
  const place = placeOf(listing, a.branchId);
  const maps = virtual ? undefined : directionsUrl(store, place);
  const price = listingPrice(listing);
  const minutes = minutesOf(a);
  const where = virtual ? 'Video call' : [store, place].filter((p) => p !== undefined).join(', ');
  const strip = appointmentStrip(a, store, now);
  const again = storeHref(listing, true);

  const facts = [
    { k: 'Reference', v: appointmentReference(a.id) },
    { k: 'For', v: user.displayName ?? user.email },
    { k: 'Price', v: price.label },
    { k: 'Payment', v: appointmentPayment(a.paymentState, price.minor) },
    { k: 'Booked', v: stamp(a.createdAt) },
  ];

  const actions: Action[] = [];
  if (upcoming) actions.push({ label: 'Reschedule', icon: IC.redo });
  if (canCancel(a, now))
    actions.push({ label: 'Cancel appointment', icon: IC.x, danger: true, act: () => setModal(true) });
  if (!upcoming && again !== undefined)
    actions.push({ label: 'Book again', icon: IC.cal, act: () => navigator.navigate(again) });
  actions.push({ label: 'Get help', icon: IC.help, act: () => navigator.navigate(helpTopicPath('appt')) });

  const addToCalendar = (): void => {
    const saved = download(
      `just-treated-${appointmentReference(a.id)}.ics`,
      appointmentIcs(a, title, store, where === '' ? store : where),
      'text/calendar',
    );
    flash(saved ? 'Calendar file downloaded' : 'Your browser could not save the calendar file');
  };

  const main = (
    <section aria-label="Booking" className="jt-bk-main">
      <div className="jt-bk-head">
        <span className="jt-bk-head__badge" style={{ color: badge.fg }}>
          {badge.label}
        </span>
        <h1 className="jt-bk-head__title">{title}</h1>
        <span className="jt-bk-head__sub">
          {store} · {virtual ? 'Video call' : 'In person'}
        </span>
      </div>

      {a.status === 'CANCELLED' ? (
        <div className="jt-bk-cancelled">
          <Icon d={IC.x} stroke="#8c1919" size={18} width={2.2} />
          <span className="jt-bk-note__text">
            <span className="jt-bk-note__head" style={{ color: '#8c1919' }}>
              Cancelled
            </span>
            <span className="jt-bk-note__body">{cancelledNote(a)}</span>
          </span>
        </div>
      ) : (
        <>
          {strip !== undefined ? (
            <div className="jt-ord-strip" style={{ background: strip.bg, color: strip.fg }}>
              <Icon d={STRIP_ICON[strip.icon]} />
              <span>{strip.text}</span>
            </div>
          ) : null}
          <div className="jt-bk-tiles">
            <div className="jt-bk-tile jt-bk-tile--when">
              <span className="jt-bk-tile__icon">
                <Icon d={IC.cal} stroke="#5b2ede" size={20} />
              </span>
              <span className="jt-bk-tile__text">
                <span className="jt-bk-tile__k jt-bk-tile__k--violet">WHEN</span>
                <span className="jt-bk-tile__v">{longDay(a.startsAt, a.timeZone)}</span>
                <span className="jt-bk-tile__s">
                  {clockOf(a.startsAt, a.timeZone)}
                  {minutes > 0 ? ` · ${String(minutes)} minutes` : ''}
                </span>
              </span>
            </div>
            <div className="jt-bk-tile">
              <span className="jt-bk-tile__icon">
                <Icon d={virtual ? IC.video : IC.pin} stroke="#5b2ede" size={20} />
              </span>
              <span className="jt-bk-tile__text">
                <span className="jt-bk-tile__k">WHERE</span>
                <span className="jt-bk-tile__v">{virtual ? 'Video call' : store}</span>
                <span className="jt-bk-tile__s">
                  {virtual ? `Online with ${store}` : (place ?? 'In the pharmacy')}
                </span>
              </span>
            </div>
          </div>
          {upcoming ? (
            <div className="jt-bk-btns">
              {virtual ? (
                <span className="jt-ord-pill jt-ord-pill--ink jt-ord-pill--off" aria-disabled="true">
                  <Icon d={IC.video} size={17} width={2.2} />
                  Join video call <Soon />
                </span>
              ) : maps !== undefined ? (
                <a
                  href={maps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="jt-ord-pill jt-ord-pill--ink"
                >
                  <Icon d={IC.pin} size={16} width={2.2} />
                  Directions
                </a>
              ) : null}
              <button type="button" className="jt-bk-outline" onClick={addToCalendar}>
                Add to calendar
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );

  const side = (
    <>
      <Aside
        storefrontId={a.storefrontId}
        store={store}
        addr={virtual ? 'Video appointment' : (place ?? 'In person')}
        facts={facts}
      />
      <Actions actions={actions} />
    </>
  );

  return (
    <>
      <DetailFrame narrow={narrow} main={main} side={side} />
      {modal ? (
        <CancelModal
          appointment={a}
          store={store}
          onClose={() => setModal(false)}
          onCancelled={() => {
            setModal(false);
            flash('Appointment cancelled');
            loaded.reload(true);
          }}
        />
      ) : null}
      <Toast message={toast} />
    </>
  );
}

// -- One consultation -------------------------------------------------------------------------

function ConsultationDetail({
  consultationId,
  user,
}: {
  readonly consultationId: string;
  readonly user: SessionUser;
}): JSX.Element {
  const navigator = useNavigator();
  const narrow = useNarrow();
  const [toast, flash] = useToast();
  const [replying, setReplying] = useState(false);
  const [qsOpen, setQsOpen] = useState(false);
  const loaded = useLoaded(async () => {
    const consultation = await getConsultation(consultationId);
    const [listing, order] = await Promise.all([
      getListing(consultation.listingId).catch(() => undefined),
      consultation.orderId !== undefined
        ? getTracking(consultation.orderId)
            .then((t) => t.order)
            .catch(() => undefined)
        : Promise.resolve(undefined),
    ]);
    return { consultation, listing, order };
  });

  const value = loaded.value;
  if (value === undefined) {
    return (
      <DetailState
        loading={loaded.loading}
        error={loaded.error}
        what="consultation"
        onRetry={() => loaded.reload()}
      />
    );
  }

  const { consultation: c, listing, order } = value;
  const now = Date.now();
  const store = listing?.storefront.name ?? 'The pharmacy';
  const state = consultationState(c, now);
  const badge = consultationBadge(c, now);
  const note = prescriberNote(c, store, now);
  const steps = consultationSteps(c, order, now);
  const price = listingPrice(listing);
  const open = consultationOpen(c, now);
  const again = storeHref(listing, false);

  const facts = [
    { k: 'Reference', v: c.reference },
    { k: 'For', v: user.displayName ?? user.email },
    { k: 'Price', v: order !== undefined ? orderTotal(order) : price.label },
    { k: 'Payment', v: consultationPayment(c, order) },
    { k: 'Submitted', v: stamp(c.submittedAt) },
  ];

  const actions: Action[] = [];
  if (c.orderId !== undefined) {
    const orderId = c.orderId;
    actions.push({ label: 'View order', icon: IC.bag, act: () => navigator.navigate(orderHref(orderId)) });
  }
  // No withdraw operation for a customer yet: cancelling the order is how.
  if (open) actions.push({ label: 'Withdraw consultation', icon: IC.x, danger: true });
  if (!open && again !== undefined)
    actions.push({ label: 'Visit the pharmacy', icon: IC.cal, act: () => navigator.navigate(again) });
  actions.push(
    state === 'MORE_INFO'
      ? { label: 'Message prescriber', icon: IC.chat, act: () => setReplying(true) }
      : { label: 'Message prescriber', icon: IC.chat },
  );
  actions.push({ label: 'Get help', icon: IC.help, act: () => navigator.navigate(helpTopicPath('appt')) });

  const main = (
    <section aria-label="Booking" className="jt-bk-main">
      <div className="jt-bk-head">
        <span className="jt-bk-head__badge" style={{ color: badge.fg }}>
          {badge.label}
        </span>
        <h1 className="jt-bk-head__title">{c.itemName}</h1>
        <span className="jt-bk-head__sub">{store} · Online consultation</span>
      </div>

      {note !== undefined ? (
        <div className="jt-bk-note" style={{ background: note.bg }}>
          <span className="jt-bk-note__avatar" aria-hidden="true">
            Rx
          </span>
          <span className="jt-bk-note__text">
            <span className="jt-bk-note__head" style={{ color: note.fg }}>
              {note.head}
            </span>
            <span className="jt-bk-note__body">{note.text}</span>
          </span>
        </div>
      ) : null}

      {state === 'SUBMITTED' ? (
        <div className="jt-bk-rx">
          <span className="jt-bk-rx__text">
            <span className="jt-bk-rx__name">{c.itemName}</span>
            <span className="jt-bk-rx__meta">
              From {store} · {price.label}
            </span>
            <span className="jt-bk-rx__meta">
              You pay first. A prescriber then reviews your answers before anything is sent; if they decline,
              you are refunded in full.
            </span>
          </span>
          {routes.checkout.availability === 'available' ? (
            <AppLink to={routes.checkout.path} className="jt-ord-pill jt-bk-violet">
              Pay and choose delivery
            </AppLink>
          ) : null}
        </div>
      ) : null}

      {c.orderId !== undefined && order !== undefined ? (
        <div className="jt-bk-rx">
          <span className="jt-bk-rx__text">
            <span className="jt-bk-rx__name">{c.itemName}</span>
            <span className="jt-bk-rx__meta">
              Order {order.reference} · {consultationPayment(c, order)}
            </span>
          </span>
          <AppLink to={orderHref(order.id)} className="jt-ord-pill jt-ord-pill--ink">
            View order
          </AppLink>
        </div>
      ) : null}

      {state === 'MORE_INFO' ? (
        <button
          type="button"
          className="jt-ord-pill jt-ord-pill--ink jt-bk-start"
          onClick={() => setReplying(true)}
        >
          Reply to prescriber
        </button>
      ) : null}

      <ol aria-label="Progress" className="jt-ord-steps">
        {steps.map((step, index) => (
          <li
            key={step.label}
            className={`jt-ord-step jt-ord-step--${step.state === 'bad' ? 'cancelled' : step.state}${
              index === steps.length - 1 ? ' jt-ord-step--last' : ''
            }`}
          >
            <span className="jt-ord-step__rail">
              <span className="jt-ord-step__dot">
                {step.state === 'done' ? <Icon d={IC.check} stroke="#fff" size={12} width={3.6} /> : null}
              </span>
              <span className="jt-ord-step__line" />
            </span>
            <span className="jt-ord-step__text">
              <span className="jt-ord-step__label">{step.label}</span>
              <span className="jt-ord-step__sub">{step.sub}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );

  const side = (
    <>
      <Aside storefrontId={c.storefrontId} store={store} addr="Online consultation" facts={facts}>
        {c.answers.length > 0 ? (
          <div className="jt-bk-qs">
            <button
              type="button"
              className="jt-bk-qs__toggle"
              aria-expanded={qsOpen}
              onClick={() => setQsOpen(!qsOpen)}
            >
              <span>Your answers</span>
              <span className={`jt-bk-qs__chev${qsOpen ? ' jt-bk-qs__chev--open' : ''}`}>
                <Icon d={IC.chevron} stroke="#17112b" size={14} width={2.6} />
              </span>
            </button>
            {qsOpen ? (
              <dl className="jt-bk-qs__list">
                {c.answers.map((answer) => (
                  <div key={answer.questionId} className="jt-bk-qs__row">
                    <dt>{answer.prompt}</dt>
                    <dd>{answerText(answer)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        ) : null}
      </Aside>
      <Actions actions={actions} />
    </>
  );

  return (
    <>
      <DetailFrame narrow={narrow} main={main} side={side} />
      {replying ? (
        <ReplyModal
          consultation={c}
          onClose={() => setReplying(false)}
          onSent={() => {
            setReplying(false);
            flash('Reply sent to your prescriber');
            loaded.reload(true);
          }}
        />
      ) : null}
      <Toast message={toast} />
    </>
  );
}

function orderTotal(order: Order): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: order.currency }).format(
      order.totalMinor / 100,
    );
  } catch {
    return `£${(order.totalMinor / 100).toFixed(2)}`;
  }
}

// -- Modals --------------------------------------------------------------------------------------

function useEscape(onClose: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

function ModalFrame({
  title,
  copy,
  icon,
  iconBg,
  iconFg,
  children,
  onClose,
}: {
  readonly title: string;
  readonly copy?: string;
  readonly icon: string;
  readonly iconBg: string;
  readonly iconFg: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
}): JSX.Element {
  useEscape(onClose);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button, textarea')?.focus();
  }, []);
  return (
    <>
      <div className="jt-ord-scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className="jt-ord-modal" role="dialog" aria-modal="true" aria-labelledby="jt-bk-m-title">
        <div className="jt-ord-modal__head">
          <span className="jt-ord-modal__icon" style={{ background: iconBg }} aria-hidden="true">
            <Icon d={icon} stroke={iconFg} size={20} width={2.2} />
          </span>
          <span className="jt-ord-modal__text">
            <span id="jt-bk-m-title" className="jt-ord-modal__title">
              {title}
            </span>
            {copy !== undefined ? <span className="jt-ord-modal__copy">{copy}</span> : null}
          </span>
        </div>
        {children}
      </div>
    </>
  );
}

function CancelModal({
  appointment,
  store,
  onClose,
  onCancelled,
}: {
  readonly appointment: Appointment;
  readonly store: string;
  readonly onClose: () => void;
  readonly onCancelled: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();

  const go = (): void => {
    if (busy) return;
    setBusy(true);
    setFailure(undefined);
    cancelAppointment(appointment.id)
      .then(() => onCancelled())
      .catch((error: unknown) => {
        setFailure(messageOf(error));
        setBusy(false);
      });
  };

  return (
    <ModalFrame
      title="Cancel this appointment?"
      copy={cancelCopy(appointment, store)}
      icon={IC.warn}
      iconBg="#fdf2e4"
      iconFg="#b25e09"
      onClose={onClose}
    >
      {failure !== undefined ? (
        <span className="jt-ord-err" role="alert">
          {failure}
        </span>
      ) : null}
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Keep appointment
        </button>
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--danger" disabled={busy} onClick={go}>
          {busy ? 'Cancelling…' : 'Cancel appointment'}
        </button>
      </div>
    </ModalFrame>
  );
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
}

/** 25.07.13: answer the prescriber — words, and the file or measurement they asked for. */
function ReplyModal({
  consultation,
  onClose,
  onSent,
}: {
  readonly consultation: Consultation;
  readonly onClose: () => void;
  readonly onSent: () => void;
}): JSX.Element {
  const asked = [...consultation.messages].reverse().find((m) => m.from === 'PRESCRIBER');
  const kind = asked?.request?.kind ?? 'TEXT';
  const [text, setText] = useState('');
  const [measure, setMeasure] = useState('');
  const [file, setFile] = useState<File | undefined>();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();

  const send = async (): Promise<void> => {
    if (busy) return;
    setFailure(undefined);
    if (kind === 'DOCUMENT' && file === undefined) {
      setFailure('Add the photo or document you were asked for.');
      return;
    }
    const value = Number(measure);
    if (kind === 'MEASUREMENT' && (measure.trim() === '' || !Number.isFinite(value) || value <= 0)) {
      setFailure(`Enter the measurement in ${asked?.request?.unit ?? 'the unit asked for'}.`);
      return;
    }
    if (kind === 'TEXT' && text.trim() === '') {
      setFailure('Write your reply.');
      return;
    }
    setBusy(true);
    try {
      let documentIds: string[] | undefined;
      if (file !== undefined) {
        if (file.size > MAX_FILE_BYTES) throw new Error('That file is too large. Send one under 10 MB.');
        const uploaded = await uploadEvidence(file.name, await readBase64(file));
        documentIds = [uploaded.id];
      }
      await replyToPrescriber(consultation.id, {
        text: text.trim(),
        ...(documentIds !== undefined ? { documentIds } : {}),
        ...(kind === 'MEASUREMENT' ? { measurement: { value } } : {}),
      });
      onSent();
    } catch (error) {
      setFailure(
        error instanceof SessionApiError || error instanceof Error ? error.message : messageOf(error),
      );
      setBusy(false);
    }
  };

  return (
    <ModalFrame
      title="Reply to your prescriber"
      {...(asked !== undefined ? { copy: asked.text } : {})}
      icon={IC.chat}
      iconBg="#f0ecfa"
      iconFg="#5b2ede"
      onClose={onClose}
    >
      <div className="jt-bk-reply">
        {kind === 'MEASUREMENT' ? (
          <label className="jt-bk-reply__field">
            <span>Measurement{asked?.request?.unit !== undefined ? ` (${asked.request.unit})` : ''}</span>
            <input
              type="number"
              inputMode="decimal"
              value={measure}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setMeasure(event.target.value)}
            />
          </label>
        ) : null}
        {kind === 'DOCUMENT' ? (
          <label className="jt-bk-reply__field">
            <span>Photo or document</span>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setFile(event.target.files?.[0] ?? undefined)
              }
            />
          </label>
        ) : null}
        <label className="jt-bk-reply__field">
          <span>{kind === 'TEXT' ? 'Your reply' : 'Anything to add (optional)'}</span>
          <textarea
            rows={4}
            maxLength={2000}
            value={text}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setText(event.target.value)}
          />
        </label>
      </div>
      {failure !== undefined ? (
        <span className="jt-ord-err" role="alert">
          {failure}
        </span>
      ) : null}
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Not now
        </button>
        <button
          type="button"
          className="jt-ord-mbtn jt-ord-mbtn--ink"
          disabled={busy}
          onClick={() => void send()}
        >
          {busy ? 'Sending…' : 'Send reply'}
        </button>
      </div>
    </ModalFrame>
  );
}
